import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lightbulb, ArrowLeft } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { useTill } from '../../../../context/TillContext';
import {
  tillSessionService,
  CASH_DENOMINATIONS,
  DenominationEntry,
} from '../../../../services/tillSessionService';
import './TillPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(amount: number): string {
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function denomLabel(d: number): string {
  return d >= 1 ? d.toLocaleString() : d.toFixed(2);
}

// ─── Component ────────────────────────────────────────────────────────────────

const TillOpenPage: React.FC = () => {
  const { t }          = useTranslation();
  const navigate       = useNavigate();
  const location       = useLocation();
  const { refreshTill } = useTill();

  const returnTo = (location.state as any)?.returnTo || '/sales/sales-invoices/create';

  // One count per denomination, initialised to 0
  const [counts, setCounts]   = useState<Record<number, number>>(
    Object.fromEntries(CASH_DENOMINATIONS.map(d => [d, 0]))
  );
  const [notes,  setNotes]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,  setError]    = useState<string | null>(null);

  // Refs for each denomination input + the notes textarea
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const notesRef  = useRef<HTMLTextAreaElement>(null);

  // Keep first denomination input focused for fast keyboard entry
  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  // Enter key advances focus through denomination inputs, then to notes
  const handleDenomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = inputRefs.current[idx + 1];
    if (next) {
      next.focus();
      next.select();
    } else {
      notesRef.current?.focus();
    }
  };

  // ─── Computed totals ──────────────────────────────────────────────────────

  const rows = useMemo<DenominationEntry[]>(
    () => CASH_DENOMINATIONS.map(d => ({
      denomination: d,
      count:        counts[d] ?? 0,
      subtotal:     d * (counts[d] ?? 0),
    })),
    [counts]
  );

  const total = useMemo(() => rows.reduce((s, r) => s + r.subtotal, 0), [rows]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleCountChange = (denom: number, raw: string) => {
    const n = parseInt(raw, 10);
    setCounts(prev => ({ ...prev, [denom]: isNaN(n) || n < 0 ? 0 : n }));
  };

  const handleSubmit = async () => {
    setError(null);
    if (total <= 0) {
      setError(t('till.openingBalanceRequired'));
      return;
    }
    setLoading(true);
    try {
      const res     = await tillSessionService.openSession({
        openingDenominations: rows,
        notes: notes.trim() || undefined,
      });
      const session = res.data?.data;
      await refreshTill();
      navigate(returnTo, {
        state: { message: t('till.openSuccess', { number: session?.sessionNumber ?? '' }) },
      });
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'TILL_ALREADY_OPEN') {
        setError(t('till.alreadyOpen'));
      } else {
        setError(t('till.openFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ims-create-body-page till-page">

        {/* Top bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}</button>
            <div>
              <h1 className="ims-page-title">{t('till.openTitle')}</h1>
              <p className="ims-page-subtitle">{t('till.openSubtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              className="btn-cancel"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              className="ims-btn ims-btn-primary"
              onClick={handleSubmit}
              disabled={loading || total <= 0}
            >
              {loading
                ? t('common.saving')
                : t('till.openTillButton', { total: fmtCurrency(total) })}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-error-banner">
            <div className="ims-alert ims-alert-error">{error}</div>
          </div>
        )}

        <div className="ims-create-body">

          {/* Left panel — denomination input table */}
          <div className="ims-panel-left" style={{ flex: '0 0 460px', maxWidth: 460 }}>
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('till.openingDenominations')}</span>
              </div>
              <div className="ims-card-body" style={{ padding: '12px 0 0' }}>
                <table className="till-denom-table">
                  <thead>
                    <tr>
                      <th>{t('till.denomination')}</th>
                      <th className="th-right">{t('till.count')}</th>
                      <th className="th-right">{t('till.subtotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row.denomination}>
                        <td>
                          <span className={`till-denom-label ${row.denomination >= 100 ? 'till-denom-label--note' : 'till-denom-label--coin'}`}>
                            {denomLabel(row.denomination)}
                          </span>
                        </td>
                        <td className="td-right">
                          <input
                            ref={el => { inputRefs.current[idx] = el; }}
                            className="till-count-input"
                            type="number"
                            min={0}
                            value={counts[row.denomination] || ''}
                            placeholder="0"
                            onChange={e => handleCountChange(row.denomination, e.target.value)}
                            onKeyDown={e => handleDenomKeyDown(e, idx)}
                          />
                        </td>
                        <td className="td-right">
                          <span className="till-subtotal">
                            {row.subtotal > 0 ? fmtCurrency(row.subtotal) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="till-total-row">
                      <td colSpan={2}>{t('till.total')}</td>
                      <td className="td-right">{fmtCurrency(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Right panel — summary + notes */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('till.openingBalance')}</span>
              </div>
              <div className="ims-card-body">
                <div className="till-summary-card">
                  <div className="till-summary-row">
                    <span className="till-summary-label">{t('till.total')}</span>
                    <span className="till-summary-value till-total-value">{fmtCurrency(total)}</span>
                  </div>
                </div>

                <div className="ims-form-group" style={{ marginTop: 8 }}>
                  <label className="ims-form-label">{t('till.notes')}</label>
                  <textarea
                    ref={notesRef}
                    className="ims-form-control"
                    rows={3}
                    value={notes}
                    placeholder={t('till.notesPlaceholder')}
                    onChange={e => setNotes(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  <p className="ims-text-muted" style={{ fontSize: 12 }}>
                    <Lightbulb size={13} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} /> {t('till.openTip')}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default TillOpenPage;
